// src/pages/WaiverPage.jsx
// Waiver wire: priority-based claims, 24h window before each week.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import { ref, onValue } from "firebase/database";
import { IPL_TEAMS, players as ALL_PLAYERS } from "../players";
import {
  waiverWindowOpen, waiverWindowLabel, calcWaiverOrder,
  submitWaiverClaim, cancelWaiverClaim, processWaiverClaims,
  subscribeWaiverClaims, MAX_SQUAD,
} from "../services/waiverApi";


function PlayerCard({ player, action, onAction, actionLabel, actionStyle = {} }) {
  const teamColor = IPL_TEAMS[player?.team]?.color || "var(--text-dim)";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "9px 14px", borderBottom: "1px solid var(--border)",
      transition: "background 0.1s",
    }}
      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {player.name}
        </div>
        <div style={{ display: "flex", gap: 5, marginTop: 3 }}>
          <span className={`role-chip role-${player.role}`}>{player.role}</span>
          <span className="team-chip" style={{ color: teamColor }}>{player.team}</span>
        </div>
      </div>
      {onAction && (
        <button onClick={() => onAction(player)} style={{ ...actionStyle, padding: "5px 12px", fontSize: 11, fontWeight: 700 }}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export function WaiverPage({ leagueCode }) {
  const user = auth.currentUser;
  const uid  = user?.uid;

  const [league,  setLeague]  = useState(null);
  const [room,    setRoom]    = useState(null);
  const [claims,  setClaims]  = useState({});
  const [status,  setStatus]  = useState("");
  const [processing, setProcessing] = useState(false);

  // UI state
  const [roleFilter,   setRoleFilter]   = useState("ALL");
  const [search,       setSearch]       = useState("");
  const [addPlayer,    setAddPlayer]    = useState(null);   // player to add
  const [dropPlayer,   setDropPlayer]   = useState(null);   // player to drop
  const [confirmOpen,  setConfirmOpen]  = useState(false);

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const u1 = onValue(ref(db, `leagues/${code}`), s => setLeague(s.val() || null));
    const u2 = onValue(ref(db, `rooms/${code}`),   s => setRoom(s.val() || null));
    const u3 = subscribeWaiverClaims(db, leagueCode, setClaims);
    return () => { u1(); u2(); u3(); };
  }, [leagueCode]);

  const isHost = useMemo(() => {
    if (!uid) return false;
    return room?.hostId === uid || league?.ownerUid === uid;
  }, [uid, room?.hostId, league?.ownerUid]);

  // My team info
  const mySlot = useMemo(() => {
    if (!uid || !room?.slots) return null;
    const entry = Object.entries(room.slots).find(([, v]) => v?.claimedBy === uid);
    return entry ? entry[0] : null;
  }, [uid, room?.slots]);

  const myRoster = useMemo(() => {
    if (!mySlot || !room) return [];
    const team = (room.teams || []).find(t => t.name === mySlot);
    if (!team?.squadIds?.length) return [];
    const pm = room.players || {};
    return team.squadIds.map(id => pm[String(id)]).filter(Boolean);
  }, [mySlot, room]);

  // All rostered player IDs (across all teams)
  const rosteredIds = useMemo(() => {
    if (!room) return new Set();
    return new Set(
      (room.teams || []).flatMap(t => (t.squadIds || []).map(String))
    );
  }, [room]);

  // Free agents = drafted players NOT on any team + ALL_PLAYERS not in room at all
  const freeAgents = useMemo(() => {
    const roomPlayerMap = room?.players || {};
    // Start with all 249 players
    return ALL_PLAYERS.filter(p => {
      // If in room, must not be rostered
      if (roomPlayerMap[String(p.id)]) return !rosteredIds.has(String(p.id));
      // If not in room at all, they're free
      return true;
    });
  }, [room, rosteredIds]);

  // Filtered free agents
  const filteredFreeAgents = useMemo(() => {
    return freeAgents.filter(p => {
      const matchRole = roleFilter === "ALL" || p.role === roleFilter;
      const matchSearch = !search.trim() || p.name.toLowerCase().includes(search.toLowerCase());
      return matchRole && matchSearch;
    });
  }, [freeAgents, search, roleFilter]);

  // My pending claims
  const myClaims = useMemo(() => {
    return Object.values(claims).filter(c => c.teamName === mySlot && c.status === "pending");
  }, [claims, mySlot]);

  // All pending (for host)
  const allPendingClaims = useMemo(() => {
    return Object.values(claims)
      .filter(c => c.status === "pending")
      .sort((a, b) => a.submittedAt - b.submittedAt);
  }, [claims]);

  const recentResults = useMemo(() => {
    return Object.values(claims)
      .filter(c => c.status !== "pending" && c.status !== "cancelled")
      .sort((a, b) => (b.processedAt || b.submittedAt) - (a.processedAt || a.submittedAt))
      .slice(0, 20);
  }, [claims]);

  const priorityOrder = useMemo(() => league ? calcWaiverOrder(league) : [], [league]);

  const windowOpen   = league ? waiverWindowOpen(league) : false;
  const windowLabel  = league ? waiverWindowLabel(league) : "—";
  const squadFull    = myRoster.length >= MAX_SQUAD;

  // ── Actions ───────────────────────────────────────────────────
  const pickPlayer = useCallback((player) => {
    setAddPlayer(player);
    setDropPlayer(null);
    setConfirmOpen(true);
    setStatus("");
  }, []);

  const submitClaim = async () => {
    if (!addPlayer || !mySlot) return;
    if (squadFull && !dropPlayer) {
      setStatus("Your squad is full (15). Select a player to drop.");
      return;
    }
    setStatus("Submitting…");
    try {
      await submitWaiverClaim(db, leagueCode, {
        teamName:     mySlot,
        addPlayerId:  addPlayer.id,
        dropPlayerId: dropPlayer?.id || null,
        week:         league?.season?.week || 1,
        addPlayerName: addPlayer.name,
        dropPlayerName: dropPlayer?.name || null,
      });
      setStatus(`✓ Claim submitted: ${addPlayer.name}${dropPlayer ? ` (drop ${dropPlayer.name})` : ""}`);
      setConfirmOpen(false);
      setAddPlayer(null);
      setDropPlayer(null);
    } catch (e) {
      setStatus("Failed: " + (e?.message || "unknown"));
    }
  };

  const cancelClaim = async (claimId) => {
    try {
      await cancelWaiverClaim(db, leagueCode, claimId);
      setStatus("Claim cancelled.");
    } catch (e) {
      setStatus("Cancel failed: " + e.message);
    }
  };

  const runWaivers = async () => {
    setProcessing(true);
    setStatus("Processing waivers…");
    try {
      const { results } = await processWaiverClaims(db, leagueCode);
      const approved = results.filter(r => r.result === "approved").length;
      const rejected = results.filter(r => r.result === "rejected").length;
      setStatus(`✓ Done: ${approved} approved, ${rejected} rejected.`);
    } catch (e) {
      setStatus("Error: " + (e?.message || "unknown"));
    } finally {
      setProcessing(false);
    }
  };

  const STATUS_COLOR = {
    approved:                "var(--green)",
    rejected_claimed:        "var(--red)",
    rejected_owned:          "var(--red)",
    rejected_no_team:        "var(--red)",
    rejected_squad_full:     "var(--red)",
    rejected_bad_drop:       "var(--red)",
    rejected_role_limit:     "var(--red)",
    rejected_one_per_window: "var(--red)",
    cancelled:               "var(--text-muted)",
  };
  const STATUS_LABEL = {
    approved:                "✓ Approved",
    rejected_claimed:        "✗ Already claimed",
    rejected_owned:          "✗ Player rostered",
    rejected_no_team:        "✗ Team not found",
    rejected_squad_full:     "✗ Squad full",
    rejected_bad_drop:       "✗ Invalid drop",
    rejected_role_limit:     "✗ Role limit",
    rejected_one_per_window: "✗ One per window",
    cancelled:               "Cancelled",
  };

  return (
    <div>
      {/* Header */}
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-head">
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Waiver Wire
            </div>
            <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 2 }}>
              {mySlot && `${mySlot} · `}
              <span style={{ color: windowOpen ? "var(--green)" : "var(--red)", fontWeight: 600 }}>
                {windowOpen ? "● Window open" : "● Window closed"}
              </span>
              <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>{windowLabel}</span>
            </div>
          </div>
          {isHost && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="primary" onClick={runWaivers} disabled={processing || allPendingClaims.length === 0}>
                {processing ? "Processing…" : `Run Waivers (${allPendingClaims.length})`}
              </button>
            </div>
          )}
        </div>
        {status && (
          <div style={{ padding: "8px 16px", fontSize: 13, borderTop: "1px solid var(--border)",
            color: status.startsWith("✓") ? "var(--green)" : status.startsWith("Failed") || status.startsWith("Error") ? "var(--red)" : "var(--gold)" }}>
            {status}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 12 }}>
        {/* ── Left: Free agents ── */}
        <div>
          {/* Filters */}
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <input className="search-box" placeholder="Search free agents…" value={search}
              onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
            {["ALL", "BAT", "BOWL", "ALR", "WK"].map(r => (
              <button key={r}
                className={roleFilter === r ? "primary" : "mini"}
                onClick={() => setRoleFilter(r)}
                style={{ fontWeight: 700, fontSize: 11 }}>
                {r}
              </button>
            ))}
          </div>

          <div className="panel">
            <div className="panel-head">
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                Free Agents
                <span className="pill muted" style={{ marginLeft: 8 }}>{filteredFreeAgents.length}</span>
              </div>
              {!windowOpen && (
                <span style={{ fontSize: 11, color: "var(--red)" }}>Window closed</span>
              )}
            </div>
            {filteredFreeAgents.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>
                No free agents match your filter.
              </div>
            ) : (
              filteredFreeAgents.map(p => (
                <PlayerCard
                  key={p.id}
                  player={p}
                  onAction={windowOpen && mySlot ? pickPlayer : null}
                  actionLabel="Claim"
                  actionStyle={{ background: "rgba(245,166,35,0.1)", color: "var(--gold)", border: "1px solid rgba(245,166,35,0.3)", borderRadius: "var(--radius-sm)" }}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Right: My claims + priority + roster ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* My pending claims */}
          <div className="panel">
            <div className="panel-head">
              <div style={{ fontWeight: 700, fontSize: 14 }}>My Claims</div>
              <span className="pill muted">{myClaims.length}</span>
            </div>
            {myClaims.length === 0 ? (
              <div style={{ padding: 14, fontSize: 13, color: "var(--text-muted)" }}>
                No pending claims.
              </div>
            ) : (
              myClaims.map(c => (
                <div key={c.id} style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--green)" }}>
                        + {c.addPlayerName}
                      </div>
                      {c.dropPlayerName && (
                        <div style={{ fontSize: 12, color: "var(--red)", marginTop: 2 }}>
                          − {c.dropPlayerName}
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>
                        Submitted {new Date(c.submittedAt).toLocaleTimeString()}
                      </div>
                    </div>
                    <button className="mini danger" onClick={() => cancelClaim(c.id)} style={{ alignSelf: "flex-start" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Waiver priority order */}
          <div className="panel">
            <div className="panel-head">
              <div style={{ fontWeight: 700, fontSize: 14 }}>Priority Order</div>
              <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Worst record first</span>
            </div>
            {priorityOrder.map((team, i) => {
              const displayName = room?.teamCustom?.[team]?.name || room?.slots?.[team]?.name || team;
              return (
              <div key={team} style={{
                padding: "8px 14px", borderBottom: "1px solid var(--border)",
                display: "flex", alignItems: "center", gap: 10,
                background: team === mySlot ? "rgba(245,166,35,0.05)" : "transparent",
              }}>
                <div className={`rank-badge ${i === 0 ? "rank-1" : ""}`} style={{ width: 22, height: 22, fontSize: 10 }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: 13, fontWeight: team === mySlot ? 700 : 500,
                  color: team === mySlot ? "var(--gold)" : "var(--text)" }}>
                  {displayName}
                  {team === mySlot && <span style={{ fontSize: 10, color: "var(--gold)", marginLeft: 6 }}>you</span>}
                </span>
              </div>
              );
            })}
          </div>

          {/* My roster */}
          {myRoster.length > 0 && (
            <div className="panel">
              <div className="panel-head">
                <div style={{ fontWeight: 700, fontSize: 14 }}>My Squad</div>
                <span className={`pill ${squadFull ? "red" : "muted"}`}>{myRoster.length}/{MAX_SQUAD}</span>
              </div>
              {myRoster.map(p => (
                <PlayerCard key={p.id} player={p} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Confirm modal ── */}
      {confirmOpen && addPlayer && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20,
        }} onClick={() => setConfirmOpen(false)}>
          <div style={{
            background: "var(--bg3)", border: "1px solid var(--border2)",
            borderRadius: "var(--radius-lg)", padding: 24, width: "100%", maxWidth: 440,
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Claim Player
            </div>

            {/* Add */}
            <div style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)",
              borderRadius: "var(--radius)", padding: "10px 14px", marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "var(--green)", marginBottom: 6 }}>ADDING</div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{addPlayer.name}</div>
              <div style={{ display: "flex", gap: 5, marginTop: 4 }}>
                <span className={`role-chip role-${addPlayer.role}`}>{addPlayer.role}</span>
                <span className="team-chip" style={{ color: IPL_TEAMS[addPlayer.team]?.color }}>{addPlayer.team}</span>
              </div>
            </div>

            {/* Drop */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 8 }}>
                {squadFull ? "Squad full — must drop a player:" : "Drop a player (optional if squad not full):"}
              </div>
              <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)" }}>
                <div
                  onClick={() => setDropPlayer(null)}
                  style={{
                    padding: "8px 12px", cursor: "pointer", fontSize: 12,
                    background: !dropPlayer ? "rgba(245,166,35,0.08)" : "transparent",
                    borderBottom: "1px solid var(--border)",
                    color: !dropPlayer ? "var(--gold)" : "var(--text-muted)",
                  }}
                >
                  {squadFull ? "— Select a player to drop —" : "— Keep full squad (no drop) —"}
                </div>
                {myRoster.map(p => (
                  <div
                    key={p.id}
                    onClick={() => setDropPlayer(p)}
                    style={{
                      padding: "8px 12px", cursor: "pointer", display: "flex", gap: 8, alignItems: "center",
                      background: dropPlayer?.id === p.id ? "rgba(248,113,113,0.08)" : "transparent",
                      borderBottom: "1px solid var(--border)",
                    }}
                    onMouseEnter={e => { if (dropPlayer?.id !== p.id) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = dropPlayer?.id === p.id ? "rgba(248,113,113,0.08)" : "transparent"; }}
                  >
                    <span className={`role-chip role-${p.role}`}>{p.role}</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</span>
                    {dropPlayer?.id === p.id && <span style={{ marginLeft: "auto", color: "var(--red)", fontSize: 11 }}>DROPPING</span>}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button className="primary" onClick={submitClaim}
                disabled={squadFull && !dropPlayer} style={{ flex: 1 }}>
                Submit Claim
              </button>
              <button onClick={() => { setConfirmOpen(false); setAddPlayer(null); setDropPlayer(null); }}>
                Cancel
              </button>
            </div>

            {squadFull && !dropPlayer && (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--red)" }}>
                Select a player to drop — squad is at max (15).
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Recent results (host only) ── */}
      {isHost && recentResults.length > 0 && (
        <div className="panel" style={{ marginTop: 12 }}>
          <div className="panel-head">
            <div style={{ fontWeight: 700, fontSize: 14 }}>Recent Waiver Results</div>
          </div>
          {recentResults.map(c => (
            <div key={c.id} style={{
              padding: "9px 14px", borderBottom: "1px solid var(--border)",
              display: "flex", gap: 10, alignItems: "center",
            }}>
              <span style={{
                fontSize: 10, fontWeight: 800, letterSpacing: "0.06em",
                color: STATUS_COLOR[c.status] || "var(--text-dim)",
                minWidth: 90,
              }}>
                {STATUS_LABEL[c.status] || c.status}
              </span>
              <div style={{ flex: 1, fontSize: 12 }}>
                <strong>{c.teamName}</strong>
                {" → "}
                {c.addPlayerName && <span style={{ color: "var(--green)" }}>+{c.addPlayerName}</span>}
                {c.dropPlayerName && <span style={{ color: "var(--red)", marginLeft: 6 }}>−{c.dropPlayerName}</span>}
              </div>
              <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                {new Date(c.processedAt || c.submittedAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── All pending claims (host only) ── */}
      {isHost && allPendingClaims.length > 0 && (
        <div className="panel" style={{ marginTop: 12 }}>
          <div className="panel-head">
            <div style={{ fontWeight: 700, fontSize: 14 }}>All Pending Claims ({allPendingClaims.length})</div>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Sorted by priority then time</span>
          </div>
          {allPendingClaims.map((c, i) => {
            const pRank = priorityOrder.indexOf(c.teamName);
            return (
              <div key={c.id} style={{
                padding: "9px 14px", borderBottom: "1px solid var(--border)",
                display: "flex", gap: 10, alignItems: "center",
              }}>
                <div className="rank-badge" style={{ width: 22, height: 22, fontSize: 10, flexShrink: 0 }}>
                  {pRank + 1}
                </div>
                <div style={{ flex: 1, fontSize: 12 }}>
                  <strong>{c.teamName}</strong>
                  {" → "}
                  <span style={{ color: "var(--green)" }}>+{c.addPlayerName}</span>
                  {c.dropPlayerName && <span style={{ color: "var(--red)", marginLeft: 6 }}>−{c.dropPlayerName}</span>}
                </div>
                <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                  {new Date(c.submittedAt).toLocaleTimeString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
